import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useMigration } from '@/lib/migration/useMigration'
import { useReadinessModule } from '@/lib/readiness/module'
import { TrackerShell } from '@/components/migration/MigrationShell'
import { MigrationItemDrawer } from '@/components/migration/MigrationItemDrawer'
import { ItemModal } from '@/components/migration/ItemModal'
import { Empty, KpiTile, StatusChip } from '@/components/migration/primitives'
import { assetIdsOf, closeOutLabel, latestRescan } from '@/lib/migration/engine'
import { STATUS_TOKEN, unassignedFindings } from '@/lib/migration/summary'
import { ITEM_STATUSES, ITEM_STATUS_LABEL, type DiscoveryComponent, type DiscoveryFinding, type MigrationItem, type MigrationItemStatus } from '@/api/types/migration'
import { capsOf, itemDependencies, monthLabel, roadmapOf } from '@/lib/catalogue/engine'
import { Chip, Icon } from '@/components/readiness/primitives'
import { FilterSelect } from '@/components/readiness/FilterSelect'
import { Toolbar, ToolbarSearch, TogglePill } from '@/components/readiness/Toolbar'
import { formatDate } from '@/components/common/format'
import { LockedPreview } from '@/components/readiness/LockedPreview'
import { useIsLocked } from '@/stores/profileStore'

// Migration Tracker · Migration items (I3 §8). A grouped table, not a tree:
// system › component › item › assets. System and Component cells span their
// rows; one row per item; an item's assets are a disclosure in the item cell.
// `?item={id}` opens the drawer; ←/→ walk the visible order. Sorting and
// filtering operate on item rows; spans are recomputed (Note 7).
//
// POC (I6 POC §5): a locked preview from the filter row down.

type GroupBy = 'system' | 'owner' | 'wave' | 'status'

// Column proportions (I4 §5.2). The brief's split sums to 100 before
// Close-out, so each column gives a little back to leave Close-out 9%.
const TRACKER_COLS: Array<[string, number]> = [['System', 11], ['Component', 13], ['Item · assets', 21], ['Current → target', 17], ['Owner', 8], ['Target', 8], ['Wave', 5], ['Status', 8], ['Close-out', 9]]
const TRACKER_COLS_FLAT: Array<[string, number]> = [['Item · assets', 21], ['System', 11], ['Component', 13], ...TRACKER_COLS.slice(3)]
type SortBy = 'order' | 'target' | 'status' | 'code'

interface Row {
  kind: 'item' | 'empty'
  item: MigrationItem | null
  system_id: string | null
  component_id: string | null
}

export default function MigrationItems() {
  const ctx = useMigration()
  const m = useReadinessModule(ctx)
  const { indexId, mscope } = ctx
  const [params, setParams] = useSearchParams()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<MigrationItemStatus | ''>('')
  const [system, setSystem] = useState('')
  const [component, setComponent] = useState('')
  const [wave, setWave] = useState('')
  const [owner, setOwner] = useState('')
  const [groupBy, setGroupBy] = useState<GroupBy>('system')
  const [sortBy, setSortBy] = useState<SortBy>('order')
  const [showEmpty, setShowEmpty] = useState(false)
  const [open, setOpen] = useState<Set<string>>(new Set())
  const [newFrom, setNewFrom] = useState<{ open: boolean; finding?: DiscoveryFinding | null }>({ open: false })
  const [pickFinding, setPickFinding] = useState(false)
  const locked = useIsLocked('migration_tracker')
  const catalogueLocked = useIsLocked('technology_catalogue')
  const gateRef = useRef<HTMLDivElement>(null)
  // The drawer never opens behind the gate, even from `?item=`.
  const selectedId = locked ? null : params.get('item')
  const q = `?index=${indexId}`

  const select = useCallback(
    (id: string | null) => {
      const p = new URLSearchParams(params)
      if (id) p.set('item', id)
      else p.delete('item')
      setParams(p, { replace: true })
    },
    [params, setParams]
  )

  const facts = mscope?.facts
  const systems = useMemo(() => facts?.systems ?? [], [facts])
  const components = useMemo(() => facts?.components ?? [], [facts])
  const compOf = (id: string | null) => components.find((c) => c.id === id) ?? null
  const sysOf = (id: string | null) => systems.find((s) => s.id === id) ?? null
  const assetOf = (id: string) => facts?.assets.find((a) => a.id === id)
  const { usage, technologies, capabilities } = m.catalogue

  // Catalogue technology with a roadmap carried by a component (§8.2 chip).
  const componentTech = useMemo(() => {
    const out = new Map<string, { id: string; name: string; date: string }>()
    components.forEach((c) => {
      const techIds = Array.from(new Set(usage.filter((u) => u.component_id === c.id).map((u) => u.technology_id)))
      for (const tid of techIds) {
        const r = roadmapOf(capsOf(tid, capabilities))
        const t = technologies.find((x) => x.id === tid)
        if (r?.roadmap_date && t) {
          out.set(c.id, { id: t.id, name: t.name, date: r.roadmap_date })
          break
        }
      }
    })
    return out
  }, [components, usage, technologies, capabilities])
  const gatedOf = useCallback((it: MigrationItem) => (mscope ? itemDependencies(it, mscope, usage, technologies, capabilities).filter((d) => d.gated) : []), [mscope, usage, technologies, capabilities])

  const items = useMemo(() => {
    if (!mscope) return []
    const s = search.toLowerCase()
    return mscope.items.filter((i) => {
      if (status && i.status !== status) return false
      if (system && i.system_id !== system) return false
      if (component && i.component_id !== component) return false
      if (wave && (i.wave_id ?? '') !== wave) return false
      if (owner && (i.owner_user_id ?? '') !== owner) return false
      if (s) {
        const assets = assetIdsOf(i.id, mscope.item_assets).map((id) => `${assetOf(id)?.code ?? id} ${assetOf(id)?.model_version ?? ''}`).join(' ')
        if (!`${i.code} ${i.title} ${i.current_algorithm} ${i.target_algorithm} ${compOf(i.component_id)?.name ?? ''} ${assets}`.toLowerCase().includes(s)) return false
      }
      return true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mscope, search, status, system, component, wave, owner])

  const sortItems = (xs: MigrationItem[]) => {
    const idx = new Map((mscope?.items ?? []).map((x, i) => [x.id, i]))
    return [...xs].sort((a, b) => {
      if (sortBy === 'target') return (a.target_date ?? '9999').localeCompare(b.target_date ?? '9999')
      if (sortBy === 'status') return ITEM_STATUSES.indexOf(a.status) - ITEM_STATUSES.indexOf(b.status)
      if (sortBy === 'code') return a.code.localeCompare(b.code)
      return (idx.get(a.id) ?? 0) - (idx.get(b.id) ?? 0)
    })
  }

  // Hierarchy rows (Group by System › Component): Discovery order of systems
  // and components; sorting applies within a component, never across groups.
  const hierarchy: Row[] = useMemo(() => {
    if (groupBy !== 'system') return []
    const rows: Row[] = []
    const sysIds = [...systems.map((s) => s.id), ...Array.from(new Set(items.map((i) => i.system_id).filter((x) => !systems.some((s) => s.id === x))))]
    const filtersActive = !!(search || status || component || wave || owner)
    sysIds.forEach((sid) => {
      if (system && sid !== system) return
      const comps: Array<DiscoveryComponent | null> = [...components.filter((c) => c.system_id === sid), null]
      comps.forEach((c) => {
        const its = sortItems(items.filter((i) => i.system_id === sid && (c ? i.component_id === c.id : !i.component_id || !compOf(i.component_id))))
        if (its.length) its.forEach((it) => rows.push({ kind: 'item', item: it, system_id: sid, component_id: c?.id ?? null }))
        else if (c && showEmpty && !filtersActive) rows.push({ kind: 'empty', item: null, system_id: sid, component_id: c.id })
      })
    })
    return rows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, items, systems, components, showEmpty, system, sortBy])

  const flatGroups = useMemo(() => {
    if (groupBy === 'system') return []
    const keyOf = (i: MigrationItem) => (groupBy === 'owner' ? i.owner_user_id ?? 'Unassigned' : groupBy === 'wave' ? facts?.waves.find((w) => w.id === i.wave_id)?.label ?? 'Unscheduled' : ITEM_STATUS_LABEL[i.status])
    const order = groupBy === 'status' ? ITEM_STATUSES.map((s) => ITEM_STATUS_LABEL[s]) : groupBy === 'wave' ? [...(facts?.waves ?? []).map((w) => w.label), 'Unscheduled'] : null
    const map = new Map<string, MigrationItem[]>()
    items.forEach((i) => map.set(keyOf(i), [...(map.get(keyOf(i)) ?? []), i]))
    const keys = Array.from(map.keys()).sort((a, b) => (order ? order.indexOf(a) - order.indexOf(b) : a.localeCompare(b)))
    return keys.map((k) => ({ key: k, items: sortItems(map.get(k)!) }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, items, facts, sortBy])

  const visibleItems = groupBy === 'system' ? hierarchy.filter((r) => r.item).map((r) => r.item!) : flatGroups.flatMap((g) => g.items)
  const order = visibleItems.map((r) => r.id)
  const selected = selectedId ? mscope?.items.find((i) => i.id === selectedId) ?? null : null
  const unassigned = mscope ? unassignedFindings(mscope) : []
  const owners = Array.from(new Set((mscope?.items ?? []).map((i) => i.owner_user_id).filter((x): x is string => !!x)))

  // KPI figures (§8.2)
  const kpi = useMemo(() => {
    if (!mscope) return null
    const all = mscope.items
    const by = Object.fromEntries(ITEM_STATUSES.map((s) => [s, all.filter((i) => i.status === s).length])) as Record<MigrationItemStatus, number>
    const sysWith = new Set(all.map((i) => i.system_id).filter(Boolean))
    const compWith = new Set(all.map((i) => i.component_id).filter(Boolean))
    const covered = new Set(mscope.item_assets.map((x) => x.asset_id))
    const gated = all.map((i) => ({ i, g: gatedOf(i) })).filter((x) => x.g.length)
    const dates = gated.flatMap((x) => x.g.map((d) => d.roadmap_date)).filter((d): d is string => !!d).sort()
    const clear = all.filter((i) => i.status !== 'not_started' && closeOutLabel(i, assetIdsOf(i.id, mscope.item_assets), mscope.item_events, mscope.rescans).tone === 'ok').length
    const awaiting = all.filter((i) => i.status === 'verified' && closeOutLabel(i, assetIdsOf(i.id, mscope.item_assets), mscope.item_events, mscope.rescans).tone === 'warn').length
    return { by, sysWith: sysWith.size, compWith: compWith.size, compWithout: components.filter((c) => !compWith.has(c.id)).length, covered: covered.size, gated: gated.length, earliest: dates[0] ?? null, clear, awaiting }
  }, [mscope, components, gatedOf])

  const closeOut = (it: MigrationItem) => {
    const scoped = assetIdsOf(it.id, mscope!.item_assets)
    const co = closeOutLabel(it, scoped, mscope!.item_events, mscope!.rescans)
    const gated = it.status !== 'closed' && co.tone !== 'ok' && co.tone !== 'warn' ? gatedOf(it)[0] : undefined
    if (gated) {
      return (
        <Link to={`/readiness/catalogue/${gated.technology.id}${q}`} onClick={(e) => e.stopPropagation()} className="rd-link" style={{ color: 'var(--rd-status-info)' }} data-testid={`closeout-${it.code}`}>
          Vendor-gated · {gated.roadmap_date ? monthLabel(gated.roadmap_date) : 'no date'}
        </Link>
      )
    }
    return (
      <span style={{ color: co.tone === 'warn' ? 'var(--rd-status-warn)' : co.tone === 'ok' ? 'var(--rd-status-ok-text)' : co.tone === 'info' ? 'var(--rd-status-info)' : 'var(--rd-text-faint)' }} data-testid={`closeout-${it.code}`}>
        {co.tone === 'warn' ? '⚠ ' : co.tone === 'ok' ? '✓ ' : ''}{co.text}
      </span>
    )
  }

  const itemCell = (it: MigrationItem) => {
    const scoped = assetIdsOf(it.id, mscope!.item_assets)
    const isOpen = open.has(it.id)
    const scan = latestRescan(it.id, mscope!.rescans)
    return (
      <td>
        <p className="truncate font-medium" title={it.title}>{it.title}</p>
        <p className="truncate text-[12px]" style={{ color: 'var(--rd-text-muted)' }}>
          <button type="button" className="rd-link" onClick={(e) => { e.stopPropagation(); const n = new Set(open); if (isOpen) n.delete(it.id); else n.add(it.id); setOpen(n) }} aria-expanded={isOpen} data-testid={`assets-toggle-${it.code}`}>{isOpen ? '▾' : '▸'} {scoped.length} {scoped.length === 1 ? 'asset' : 'assets'}</button> · <span className="rd-mono">{it.code}</span>{it.created_from_group ? ' · group' : ''}{it.target_source === 'catalogue' ? ' · target follows catalogue' : ''}
        </p>
        {isOpen && (
          <ul className="mt-1.5 space-y-0.5 text-[11.5px]" data-testid={`assets-${it.code}`}>
            {scoped.map((id) => {
              const a = assetOf(id)
              const o = scan?.outcomes.find((x) => x.asset_id === id)
              return (
                <li key={id} className="flex items-center gap-2">
                  <span className="rd-mono">{a?.code ?? id}</span>
                  <span className="min-w-0 flex-1 truncate" style={{ color: 'var(--rd-text-muted)' }}>{a?.model_version}</span>
                  {o && <Chip tone={o.vulnerable_present ? 'warn' : 'ok'}>{o.vulnerable_present ? 'still vulnerable' : 're-scan clear'}</Chip>}
                </li>
              )
            })}
          </ul>
        )}
      </td>
    )
  }
  const rest = (it: MigrationItem, withSystem = false) => (
    <>
      {withSystem && <td className="truncate font-bold" title={sysOf(it.system_id)?.name}>{sysOf(it.system_id)?.name ?? '—'}</td>}
      {withSystem && <td className="truncate font-semibold" title={compOf(it.component_id)?.name}>{compOf(it.component_id)?.name ?? '—'}</td>}
      <td className="rd-mono rd-cell-ellipsis text-[12.5px]" title={`${it.current_algorithm || '—'} → ${it.target_algorithm || 'TBD'}`}>{it.current_algorithm || '—'} → {it.target_algorithm || 'TBD'}</td>
      <td className="truncate" title={it.owner_user_id ?? undefined}>{it.owner_user_id ?? '—'}</td>
      <td className="rd-mono whitespace-nowrap text-[12.5px]">{it.target_date ?? '—'}</td>
      <td className="whitespace-nowrap">{facts?.waves.find((x) => x.id === it.wave_id)?.label ?? <span style={{ color: 'var(--rd-text-faint)' }}>Unscheduled</span>}</td>
      <td><StatusChip status={it.status} /></td>
      <td className="text-[12.5px]">{closeOut(it)}</td>
    </>
  )
  const rowProps = (it: MigrationItem, extra: React.CSSProperties = {}) => ({
    onClick: () => select(it.id),
    className: 'cursor-pointer',
    style: { borderBottom: '1px solid var(--rd-border-subtle)', background: it.id === selectedId ? 'var(--rd-bg-tile)' : undefined, ...extra },
    'data-testid': `item-row-${it.code}`,
    'aria-selected': it.id === selectedId,
  })
  const groupCell = (children: ReactNode, span: number, testId: string, topBorder: boolean) => (
    <td rowSpan={span} style={{ background: 'var(--rd-bg-header)', borderRight: '1px solid var(--rd-border-subtle)', borderTop: topBorder ? '2px solid var(--rd-border-control)' : undefined }} data-testid={testId}>{children}</td>
  )

  const fromFindings = (
    <span className="relative">
      <button type="button" className="rd-btn" onClick={() => setPickFinding((v) => !v)} disabled={locked || !unassigned.length} style={locked ? { opacity: 0.4 } : undefined} data-testid="create-from-findings"><Icon name="plus" size={14} /> Create from Discovery findings <span className="rd-mono" style={{ color: 'var(--rd-status-warn)' }}>{unassigned.length}</span></button>
      {pickFinding && (
        <div role="listbox" aria-label="Unassigned findings" className="rd-card absolute right-0 top-full z-20 mt-1 max-h-72 w-96 overflow-y-auto p-1 text-[12.5px]" style={{ boxShadow: 'var(--rd-shadow-float)', border: '1px solid var(--rd-border-control)' }} data-testid="finding-list">
          {unassigned.map((f) => (
            <button key={f.id} type="button" role="option" aria-selected={false} className="flex w-full items-start gap-2 rounded px-3 py-1.5 text-left hover:underline" onClick={() => { setPickFinding(false); setNewFrom({ open: true, finding: f }) }}>
              <Chip tone={f.severity === 'high' ? 'warn' : 'neutral'}>{f.severity}</Chip>
              <span className="min-w-0 flex-1"><span className="block truncate">{f.title}</span><span style={{ color: 'var(--rd-text-faint)' }}>{sysOf(f.system_id)?.name} › {compOf(assetOf(f.asset_ids[0])?.component_id ?? null)?.name ?? '—'} · {f.asset_ids.length} {f.asset_ids.length === 1 ? 'asset' : 'assets'} · {formatDate(f.found_at)}</span></span>
            </button>
          ))}
        </div>
      )}
    </span>
  )

  return (
    <TrackerShell ctx={ctx} onNewItem={() => setNewFrom({ open: true })} onFromFindings={fromFindings} testId="migration-items">
      {mscope && kpi && (
        <LockedPreview feature="migration_tracker" gateAt={gateRef} title="Migration Tracker" body="Plan and track every migration against the CBOM, with vendor-gated gaps, owners and target dates rolled up into the readiness score.">
          <div className="mb-4 grid grid-cols-2 gap-3 xl:grid-cols-4" data-testid="tracker-kpis">
            <KpiTile
              label="Items by status"
              value={mscope.items.length}
              sub={
                <span className="block">
                  <span className="mt-1 flex w-full overflow-hidden rounded-full" style={{ height: 8, gap: 2 }} role="img" aria-label={ITEM_STATUSES.map((s) => `${ITEM_STATUS_LABEL[s]} ${kpi.by[s]}`).join(', ')}>
                    {ITEM_STATUSES.map((s) => (kpi.by[s] ? <span key={s} style={{ flex: kpi.by[s], background: STATUS_TOKEN[s] }} /> : null))}
                  </span>
                  <span className="mt-1 block">{ITEM_STATUSES.filter((s) => kpi.by[s]).map((s) => `${kpi.by[s]} ${ITEM_STATUS_LABEL[s].toLowerCase()}`).join(' · ')}</span>
                </span>
              }
            />
            <KpiTile label="Coverage" value={<span data-testid="kpi-coverage">{kpi.sysWith} <span className="font-sans text-[14px] font-medium" style={{ color: 'var(--rd-text-muted)' }}>systems</span> · {kpi.compWith} <span className="font-sans text-[14px] font-medium" style={{ color: 'var(--rd-text-muted)' }}>components</span></span>} sub={<>{kpi.covered} assets under an item · <span style={{ color: unassigned.length ? 'var(--rd-status-warn)' : undefined }}>{unassigned.length} findings unassigned</span></>} />
            <KpiTile label="Vendor-gated" value={<span data-testid="kpi-gated">{kpi.gated}</span>} sub={<>{kpi.earliest ? `earliest vendor date ${monthLabel(kpi.earliest)} · ` : ''}{catalogueLocked ? <span>Catalogue →</span> : <Link to={`/readiness/catalogue${q}`} className="rd-link">Catalogue →</Link>}</>} />
            <KpiTile label="Close-out" value={<>{kpi.clear} clear</>} sub={kpi.awaiting ? <span style={{ color: 'var(--rd-status-warn)' }}>{kpi.awaiting} awaiting re-scan</span> : 'no item awaiting re-scan'} />
          </div>

          <Toolbar className="mb-3" testId="tracker-toolbar" innerRef={gateRef}>
            <ToolbarSearch label="Search items, components and assets" placeholder="Search items, components, assets" value={search} onChange={setSearch} />
            <FilterSelect any label="Status" value={status} onChange={(v) => setStatus(v as MigrationItemStatus | '')} options={ITEM_STATUSES.map((s) => [s, ITEM_STATUS_LABEL[s]])} />
            <FilterSelect any label="System" value={system} onChange={(v) => { setSystem(v); setComponent('') }} options={systems.map((s) => [s.id, s.name])} />
            <FilterSelect any label="Component" value={component} onChange={setComponent} options={components.filter((c) => !system || c.system_id === system).map((c) => [c.id, c.name])} />
            <FilterSelect any label="Wave" value={wave} onChange={setWave} options={(facts?.waves ?? []).map((w) => [w.id, w.label])} />
            <FilterSelect any label="Owner" value={owner} onChange={setOwner} options={owners.map((o) => [o, o])} />
            <FilterSelect label="Group by" value={groupBy} onChange={(v) => setGroupBy(v as GroupBy)} options={[['system', 'System › Component'], ['owner', 'Owner'], ['wave', 'Wave'], ['status', 'Status']]} />
            <FilterSelect label="Sort" value={sortBy} onChange={(v) => setSortBy(v as SortBy)} options={[['order', 'Created'], ['target', 'Target date'], ['status', 'Status'], ['code', 'Code']]} />
          </Toolbar>

          {visibleItems.length === 0 && !hierarchy.length ? (
            <Empty>{mscope.items.length ? 'No items match the current filters.' : 'No migration items yet. Create one from a Discovery finding, a CBOM asset group, or blank.'}</Empty>
          ) : (
            <div className="rd-card overflow-x-auto">
              {/* Fixed columns (I4 §5.2); below 1100px the wrapper scrolls instead of squeezing. */}
              <table className="rd-table-fixed w-full text-[13.5px]" style={{ minWidth: 1100 }} data-testid="items-table">
                <colgroup>
                  {(groupBy === 'system' ? TRACKER_COLS : TRACKER_COLS_FLAT).map(([h, w]) => <col key={h} style={{ width: `${w}%` }} />)}
                </colgroup>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--rd-border-subtle)' }}>
                    {(groupBy === 'system' ? TRACKER_COLS : TRACKER_COLS_FLAT).map(([h]) => <th key={h} className="text-left">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {groupBy === 'system'
                    ? hierarchy.map((r, idx) => {
                        const firstSys = idx === 0 || hierarchy[idx - 1].system_id !== r.system_id
                        const firstComp = firstSys || hierarchy[idx - 1].component_id !== r.component_id
                        const sysSpan = hierarchy.filter((x) => x.system_id === r.system_id).length
                        const compSpan = hierarchy.filter((x) => x.system_id === r.system_id && x.component_id === r.component_id).length
                        const sys = sysOf(r.system_id)
                        const comp = compOf(r.component_id)
                        const tech = r.component_id ? componentTech.get(r.component_id) : undefined
                        const nItems = hierarchy.filter((x) => x.system_id === r.system_id && x.item).length
                        const top = firstSys && idx > 0
                        const cells = (
                          <>
                            {firstSys && groupCell(<><p className="text-[14.5px] font-bold leading-snug">{sys?.name ?? (r.system_id ? 'Unknown system' : 'No system')}</p><p className="rd-mono text-[12px]" style={{ color: 'var(--rd-text-muted)' }}>{nItems} {nItems === 1 ? 'item' : 'items'}</p></>, sysSpan, `system-group-${r.system_id ?? 'none'}`, top)}
                            {firstComp && groupCell(
                              <>
                                <p className="text-[13.5px] font-semibold leading-snug">{comp?.name ?? <span style={{ color: 'var(--rd-text-faint)' }}>No component</span>}</p>
                                {tech && <Link to={`/readiness/catalogue/${tech.id}${q}`} onClick={(e) => e.stopPropagation()} className="rd-chip mt-1 inline-flex max-w-full" style={{ background: 'var(--rd-coverage-bg)', color: 'var(--rd-coverage)' }} title={tech.name} data-testid={`catalogue-chip-${r.component_id}`}><span className="truncate">In catalogue · PQC {monthLabel(tech.date)} ↗</span></Link>}
                              </>,
                              compSpan,
                              `component-group-${r.component_id ?? 'none'}`,
                              top
                            )}
                          </>
                        )
                        if (r.kind === 'empty') {
                          return (
                            <tr key={`empty-${r.component_id}`} style={{ borderBottom: '1px solid var(--rd-border-subtle)', borderTop: top ? '2px solid var(--rd-border-control)' : undefined }} data-testid={`empty-component-${r.component_id}`}>
                              {cells}
                              <td colSpan={7} className="text-[12.5px]" style={{ color: 'var(--rd-text-faint)' }}>No migration item · {(facts?.assets ?? []).filter((a) => a.component_id === r.component_id).length} assets detected by Discovery</td>
                            </tr>
                          )
                        }
                        const it = r.item!
                        return (
                          <tr key={it.id} {...rowProps(it, top ? { borderTop: '2px solid var(--rd-border-control)' } : {})}>
                            {cells}
                            {itemCell(it)}
                            {rest(it)}
                          </tr>
                        )
                      })
                    : flatGroups.flatMap((g) => [
                        <tr key={`g-${g.key}`} style={{ background: 'var(--rd-bg-header)', borderTop: '2px solid var(--rd-border-control)' }}>
                          <td colSpan={9} className="text-[12.5px] font-semibold" data-testid={`flat-group-${g.key}`}>{g.key} <span className="rd-mono font-normal" style={{ color: 'var(--rd-text-muted)' }}>· {g.items.length}</span></td>
                        </tr>,
                        ...g.items.map((it) => (
                          <tr key={it.id} {...rowProps(it)}>
                            {itemCell(it)}
                            {rest(it, true)}
                          </tr>
                        )),
                      ])}
                </tbody>
              </table>
            </div>
          )}
          {/* The toggle sits on the footer row so the toolbar keeps one row at 1440 (I4 §3). */}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }} data-testid="tracker-footer">
            {groupBy === 'system' && <TogglePill pressed={showEmpty} onChange={setShowEmpty} testId="show-empty-components">Show components without items</TogglePill>}
            <span>{mscope.items.length} items across {kpi.sysWith} systems · {kpi.compWith} components with items · {kpi.compWithout} components without</span>
            <span>· click a row to open the drawer · ←/→ move between items, Esc closes</span>
          </div>
        </LockedPreview>
      )}

      {selected && (
        <MigrationItemDrawer ctx={ctx} item={selected} prev={order[order.indexOf(selected.id) - 1] ?? null} next={order[order.indexOf(selected.id) + 1] ?? null} onNavigate={select} onClose={() => select(null)} />
      )}
      {newFrom.open && !locked && <ItemModal ctx={ctx} finding={newFrom.finding} onClose={() => setNewFrom({ open: false })} onCreated={(it) => { setNewFrom({ open: false }); select(it.id) }} />}
    </TrackerShell>
  )
}
